import { AxiosResponse } from "axios";
import { axiosRequest } from "../api.utils";
import { API_ROOT } from "services/baseURLs.config";
import type { CompanyWorker } from "model/Company";

const getSalaries = async (
  companyId: number,
  page: number,
  pageSize: number,
  year: number,
  month: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/salaries?page=${page}&pageSize=${pageSize}&year=${year}&month=${month}`
  );
};

const generateSalaryNotice = async (
  companyId: number,
  workerId: number,
  pdfId: number
): Promise<AxiosResponse<Blob>> => {
  return await axiosRequest.auth.get<Blob>(
    `${API_ROOT}/companies/${companyId}/salaries/workers/${workerId}/pdf/${pdfId}`,
    {
      headers: {
        "Content-Type": "application/pdf",
      },
      responseType: "blob",
    }
  );
};

const getCompanyWorkers = async (
  companyId: number,
  active: boolean
): Promise<AxiosResponse<CompanyWorker[]>> => {
  return await axiosRequest.auth.get<CompanyWorker[]>(
    `${API_ROOT}/companies/${companyId}/workers/v2/list?active=${active}`
  );
};

const getWorkerSalaries = async (
  companyId: number,
  workerId: number,
  year?: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/salaries`,
    {
      params: {
        workerId,
        year,
      },
    }
  );
};

const getAGI = async (
  companyId: number,
  year: number,
  month: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<Blob>(
    `${API_ROOT}/companies/${companyId}/salaries/employerPayrollReport/${year}-${month}`,
    {
      headers: {
        "Content-Type": "application/xml",
      },
      responseType: "blob",
    }
  );
};

const getWorkersAGI = async (
  companyId: number,
  year: number,
  month: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/salaries/employerPayrollReport/${year}-${month}/workers`
  );
};

const getCompanyWorkHours = async (
  companyId: number,
  year: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/monthlyWorkHours/${year}`
  );
};

const overrideMonthWorkingHours = async (
  companyId: number,
  period: string,
  hours: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/monthlyWorkHours`,

    {
      period,
      hours,
    }
  );
};

const getAllSalaries = async (
  companyId: number,
  page: number,
  pageSize: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/workers/v2?page=${page}&pageSize=${pageSize}&workGroupFilter=All`
  );
};

const updateWorkerSalary = async (
  companyId: number,
  workerId: number,
  startDate: string,
  employmentType: string,
  salary: number,
  salaryType: string,
  workPercentage: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v2`,
    {
      startDate,
      employmentType,
      salary,
      salaryType,
      workPercentage,
    }
  );
};

const updateWorkerEmployment = async (
  companyId: number,
  workerId: number,
  startDate: string,
  employmentType: string,
  salary: number,
  salaryType: string,
  workPercentage: number,
  taxTable: number,
  paymentType: string,
  role: string,
  endDate: string | null,
  employeeCategory: string,
  employeeNumber: number
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<any>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v2/employment`,
    {
      startDate,
      employmentType,
      salary,
      salaryType,
      workPercentage,
      taxTable,
      paymentType,
      role,
      endDate,
      employeeCategory,
      employeeNumber,
    }
  );
};

const generateHogiaFile = async (
  companyId: number,
  year: number,
  month: number
): Promise<AxiosResponse<Blob>> => {
  return axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/export/hogia/${year}-${month}`,
    null,
    {
      responseType: "blob",
      headers: {
        Accept: "application/zip",
      },
    }
  );
};

const generateSIEFile = (companyId: number, year: number, month: number) =>
  axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/export/sie4/${year}-${month}`,
    null,
    {
      responseType: "blob",
      headers: { Accept: "text/plain, application/octet-stream, */*" },
    }
  );

export const salary = {
  getSalaries,
  generateSalaryNotice,
  getCompanyWorkers,
  getWorkerSalaries,
  getAGI,
  getWorkersAGI,
  getCompanyWorkHours,
  overrideMonthWorkingHours,
  getAllSalaries,
  updateWorkerSalary,
  updateWorkerEmployment,
  generateHogiaFile,
  generateSIEFile,
};
